import { push } from "connected-react-router";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import routes from "./routes";

export function useRouterCore() {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const history = useHistory();

  const goTo = useCallback(
    async (route: string) => {
      try {
        dispatch(push(route));
      } catch (e) {
        console.error(e);
      }
    },
    [dispatch]
  );

  return {
    goToLogin: () => goTo(routes.login),
    goToBinding: () => goTo(routes.binding),
    gotoEmails: () => goTo(routes.emails),
    goToCreateAccount: () => goTo(routes.createAccount),
    goToPlans: () => goTo(routes.plans),
    goToUsers: () => goTo(routes.users),
    goToUserProfile: (email?: string) => {
      const trimmed = String(email || "").trim();
      if (!trimmed) return goTo(routes.users);
      return goTo(
        `${routes.users}?email=${encodeURIComponent(trimmed)}`
      );
    },
    goToWembiiAdminDashboard: () => goTo(routes.wembiiAdminDashboard),
    goToWembiiAdminSupport: () => goTo(routes.wembiiAdminSupport),
    goToWembiiAdminSendGridBilling: () => goTo(routes.wembiiAdminSendGridBilling),
    goToCompany: () => goTo(routes.company),
    goToMeCompany: () => goTo(routes.meCompany),
    goToSocial: () => goTo(routes.social),
    goToHomeDashboard: () => goTo(routes.homeDashboard),
    goToAnalyticsSummary: () => goTo(routes.analyticsSummary),
    goToFacebookAnalytics: () => goTo(routes.facebookAnalytics),
    goToInstagramAnalytics: () => goTo(routes.instagramAnalytics),
    goToTiktokAnalytics: () => goTo(routes.tiktokAnalytics),
    goToLinkedInBusinessAnalytics: () => goTo(routes.linkedinBusinessAnalytics),
    goToYoutubeAnalytics: () => goTo(routes.youtubeAnalytics),
    goToGoogleAnalytics: () => goTo(routes.googleAnalytics),
    goToWooCommerceAnalytics: () => goTo(routes.wooCommerceAnalytics),
    goToMercadolibreAnalytics: () => goTo(routes.mercadolibreAnalytics),
    goToWooCommerceOrders: () => goTo(routes.wooCommerceOrders),
    goToMercadolibreOrders: () => goTo(routes.mercadolibreOrders),
    goToJumpsellerOrders: () => goTo(routes.jumpsellerOrders),
    goToSocialStreams: () => goTo(routes.socialStreams),
    goToSocialPost: (from: string, repost = false) =>
      repost
        ? goTo(`${routes.socialPost}?from=${from}&repost=${repost}`)
        : goTo(`${routes.socialPost}?from=${from}`),
    /** Posteador con imagen pendiente (IA / texto a imagen) */
    goToSocialPostFromAiImage: () => goTo(`${routes.socialPost}?from=ai-image`),
    goToSocialAutomatedPosts: () => goTo(routes.socialAutomatedPosts),
    goToAIGeneration: () => goTo(routes.aiGeneration),
    goToTextToImage: () => goTo(routes.aiTextToImage),
    goToSocialStory: (from: string) =>
      goTo(`${routes.socialStory}?from=${from}`),
    goToSocialHistory: () => goTo(routes.socialHistory),
    goToSocialManager: () => goTo(routes.socialManager),
    goToSocialContentGrid: () => goTo(routes.socialContentGrid),
    goToSocialCalendar: () => goTo(routes.socialCalendar),
    goToVideoGen: () => goTo(routes.videoGen),
    goToVideoEditor: () => goTo(routes.videoEditor),
    goToEditCompany: (id: string, from?: string) =>
      goTo(`${routes.company}/${id}?from=${from}`),
    goToWebForms: () => goTo(routes.webForms),
    goToEditWebForm: (id: string) => goTo(`${routes.webForms}/${id}`),
    goToFacebookForms: () => goTo(routes.facebookForm),
    goToViewFacebookForm: (id: string) => goTo(`${routes.facebookForm}/${id}`),
    goToEditWebChat: () => goTo(routes.editWebChat),
    goToWebChat: () => goTo(routes.webChat),
    goToEditWhatsappChat: () => goTo(routes.editWhatsappChat),
    goToFacebookCampaigns: () => goTo(routes.facebookCampaigns),
    goToCreateCampaign: () => goTo(routes.createCampaign),
    goToContacts: () => goTo(routes.contacts),
    goToContactCompanies: () => goTo(routes.contactCompanies),
    goToReports: () => goTo(routes.reports),
    goToResellers: () => goTo(routes.resellers),
    goToActivityBoards: () => goTo(routes.activityBoards),
    goToBusinessBoard: () => goTo(routes.businessBoard),
    goToTasks: () => goTo(routes.tasks),
    goToBusinessBoardAnalytics: () => goTo(routes.businessBoardAnalytics),
    goToEditBoard: (id: string) => goTo(`${routes.boards}/${id}`),
    goToEditActivityBoard: (id: string) =>
      goTo(`${routes.activityBoards}/${id}`),
    goToAutomations: () => goTo(routes.automations),
    goToCalendar: () => goTo(routes.calendar),
    goToNotifications: () => goTo(routes.notifications),
    goToWebsitePages: () => goTo(routes.websitePages),
    goToEditWebsitePage: (id: string) => goTo(`${routes.websitePages}/${id}`),
    goToEmailMarketingLists: () => goTo(routes.emailMarketingLists),
    goToEmailMarketingEmails: () => goTo(routes.emailMarketingEmails),
    goToEmailMarketingEmailsFromTemplate: (templateId: string) =>
      goTo(
        `${routes.emailMarketingEmails}?templateId=${encodeURIComponent(templateId)}`
      ),
    goToEmailMarketingCampaign: (campaignId: string) =>
      goTo(routes.emailMarketingCampaignDetail.replace(":campaignId", campaignId)),
    goToEmailMarketingTemplates: () => goTo(routes.emailMarketingTemplates),
    goToEmailMarketingReports: () => goTo(routes.emailMarketingReports),
    goToEmailMarketingConfig: () => goTo(routes.emailMarketingConfig),

    routes,
    pathname,
    search,
    history,
  };
}
